/**
 * Atlas Web — route registry.
 *
 * Every screen in the REQUIRED PAGE INVENTORY (100 pages) is wired here.
 * `public` routes render outside the app shell (auth screens);
 * `shell` routes render inside AppShell; all others require auth + tenant.
 */
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";

// ---- Auth (1-4) -----------------------------------------------------------
import { LoginPage } from "@/features/auth/LoginPage";
import { MfaChallengePage } from "@/features/auth/MfaChallengePage";
import { SignUpPage } from "@/features/auth/SignUpPage";
import { VerifyEmailPage } from "@/features/auth/VerifyEmailPage";
import { WorkspaceCreationPage } from "@/features/auth/WorkspaceCreationPage";

// ---- Shell (5-10, 16 overlay) ----------------------------------------------
import { HomeDashboardPage } from "@/features/shell/HomeDashboardPage";
import { InboxPage } from "@/features/shell/InboxPage";
import { SearchResultsPage } from "@/features/shell/SearchResultsPage";
import { NotificationsPage } from "@/features/shell/NotificationsPage";
import { ProfilePage } from "@/features/shell/ProfilePage";

// ---- Setup / Admin (11-15, 88-100) ------------------------------------------
import { SetupCenterPage } from "@/features/setup/SetupCenterPage";
import { ImplementationWizardPage } from "@/features/setup/ImplementationWizardPage";
import { AuditLogExplorerPage } from "@/features/setup/AuditLogExplorerPage";
import { MarketplacePage } from "@/features/setup/MarketplacePage";
import { ApiWebhooksPage } from "@/features/setup/ApiWebhooksPage";
import { UserManagementPage } from "@/features/setup/UserManagementPage";
import { RolesPermissionsPage } from "@/features/setup/RolesPermissionsPage";
import { WorkflowBuilderPage } from "@/features/setup/WorkflowBuilderPage";
import { ApprovalMatrixDesignerPage } from "@/features/setup/ApprovalMatrixDesignerPage";
import { EntityBuilderPage } from "@/features/setup/EntityBuilderPage";
import { FormViewDesignerPage } from "@/features/setup/FormViewDesignerPage";
import { CountryPackManagerPage } from "@/features/setup/CountryPackManagerPage";
import { IndustryPackManagerPage } from "@/features/setup/IndustryPackManagerPage";
import { IntegrationCenterPage } from "@/features/setup/IntegrationCenterPage";
import { DataImportCenterPage } from "@/features/setup/DataImportCenterPage";
import { TenantSettingsPage } from "@/features/setup/TenantSettingsPage";
import { SecurityCenterPage } from "@/features/setup/SecurityCenterPage";
import { BillingPage } from "@/features/setup/BillingPage";

// ---- Finance (17-34) -------------------------------------------------------
import { FinanceDashboardPage } from "@/features/finance/FinanceDashboardPage";
import { ChartOfAccountsPage } from "@/features/finance/ChartOfAccountsPage";
import { JournalEntriesPage } from "@/features/finance/JournalEntriesPage";
import { JournalEntryDetailPage } from "@/features/finance/JournalEntryDetailPage";
import { GeneralLedgerPage } from "@/features/finance/GeneralLedgerPage";
import { TrialBalancePage } from "@/features/finance/TrialBalancePage";
import { FinancialStatementsPage } from "@/features/finance/FinancialStatementsPage";
import { CustomerInvoicesPage } from "@/features/finance/CustomerInvoicesPage";
import { SupplierInvoicesPage } from "@/features/finance/SupplierInvoicesPage";
import { PaymentsPage } from "@/features/finance/PaymentsPage";
import { BankReconciliationPage } from "@/features/finance/BankReconciliationPage";
import { CashManagementPage } from "@/features/finance/CashManagementPage";
import { FixedAssetsPage } from "@/features/finance/FixedAssetsPage";
import { TaxDashboardPage } from "@/features/finance/TaxDashboardPage";
import { PeriodClosePage } from "@/features/finance/PeriodClosePage";
import { ExchangeRatesPage } from "@/features/finance/ExchangeRatesPage";
import { BudgetingPage } from "@/features/finance/BudgetingPage";
import { ConsolidationPage } from "@/features/finance/ConsolidationPage";

// ---- Sales (35-45) ----------------------------------------------------------
// 35 Leads and 36 Opportunities are the CRM pages (CRM-W01); see below.
import { QuotationsPage } from "@/features/sales/QuotationsPage";
import { SalesOrdersPage } from "@/features/sales/SalesOrdersPage";
import { CustomersPage } from "@/features/sales/CustomersPage";
import { DeliveryNotesPage } from "@/features/sales/DeliveryNotesPage";
import { SalesInvoicesPage } from "@/features/sales/SalesInvoicesPage";
import { ReturnsPage } from "@/features/sales/ReturnsPage";
import { PriceListsPage } from "@/features/sales/PriceListsPage";
import { CreditLimitReviewPage } from "@/features/sales/CreditLimitReviewPage";
import { CommissionsPage } from "@/features/sales/CommissionsPage";

// ---- Procurement (46-53) -----------------------------------------------------
import { PurchaseRequestsPage } from "@/features/procurement/PurchaseRequestsPage";
import { RfqsPage } from "@/features/procurement/RfqsPage";
import { PurchaseOrdersPage } from "@/features/procurement/PurchaseOrdersPage";
import { GoodsReceiptsPage } from "@/features/procurement/GoodsReceiptsPage";
import { SupplierInvoicesProcurementPage } from "@/features/procurement/SupplierInvoicesPage";
import { SuppliersPage } from "@/features/procurement/SuppliersPage";
import { SupplierEvaluationPage } from "@/features/procurement/SupplierEvaluationPage";
import { ProcurementContractsPage } from "@/features/procurement/ProcurementContractsPage";

// ---- Inventory (54-62) --------------------------------------------------------
import { ProductsPage } from "@/features/inventory/ProductsPage";
import { StockByLocationPage } from "@/features/inventory/StockByLocationPage";
import { InventoryMovementsPage } from "@/features/inventory/InventoryMovementsPage";
import { TransfersPage } from "@/features/inventory/TransfersPage";
import { AdjustmentsPage } from "@/features/inventory/AdjustmentsPage";
import { CycleCountsPage } from "@/features/inventory/CycleCountsPage";
import { TraceabilityPage } from "@/features/inventory/TraceabilityPage";
import { ReorderPlannerPage } from "@/features/inventory/ReorderPlannerPage";
import { ValuationReportPage } from "@/features/inventory/ValuationReportPage";

// ---- Manufacturing (63-67) -----------------------------------------------------
import { BomsPage } from "@/features/manufacturing/BomsPage";
import { WorkCentersPage } from "@/features/manufacturing/WorkCentersPage";
import { ProductionOrdersPage } from "@/features/manufacturing/ProductionOrdersPage";
import { MrpPlannerPage } from "@/features/manufacturing/MrpPlannerPage";
import { QualityInspectionsPage } from "@/features/manufacturing/QualityInspectionsPage";

// ---- HR & Payroll (68-73) --------------------------------------------------------
import { EmployeeDirectoryPage } from "@/features/hr/EmployeeDirectoryPage";
import { EmployeeRecordPage } from "@/features/hr/EmployeeRecordPage";
import { LeaveRequestsPage } from "@/features/hr/LeaveRequestsPage";
import { TimeAttendancePage } from "@/features/hr/TimeAttendancePage";
import { PayrollRunsPage } from "@/features/hr/PayrollRunsPage";
import { PayslipsPage } from "@/features/hr/PayslipsPage";

// ---- Projects (74-77) -------------------------------------------------------------
import { ProjectsPage } from "@/features/projects/ProjectsPage";
import { TimesheetsPage } from "@/features/projects/TimesheetsPage";
import { ExpenseReportsPage } from "@/features/projects/ExpenseReportsPage";
import { AssetMaintenancePage } from "@/features/projects/AssetMaintenancePage";

// ---- Services (78-79) ----------------------------------------------------------------
import { ServiceTicketsPage } from "@/features/services/ServiceTicketsPage";
import { ServiceAppointmentsPage } from "@/features/services/ServiceAppointmentsPage";

// ---- POS (80-81) ---------------------------------------------------------------------
import { PosSessionsPage } from "@/features/pos/PosSessionsPage";
import { PosOrderPage } from "@/features/pos/PosOrderPage";

// ---- Logistics & Fleet (82-83) --------------------------------------------------------
import { ShipmentsPage } from "@/features/logistics/ShipmentsPage";
import { FleetVehiclesPage } from "@/features/logistics/FleetVehiclesPage";

// ---- Documents (84) ----------------------------------------------------------------------
import { DocumentLibraryPage } from "@/features/documents/DocumentLibraryPage";

// ---- Reports & BI (85-87) -------------------------------------------------------------------
import { DashboardBuilderPage } from "@/features/reports/DashboardBuilderPage";
import { ReportBuilderPage } from "@/features/reports/ReportBuilderPage";
import { PivotReportViewerPage } from "@/features/reports/PivotReportViewerPage";

export interface RouteDef {
  path: string;
  element: ReactElement;
  public?: boolean;
  shell?: boolean;
  /**
   * The permission this screen needs, which is the permission its data needs.
   *
   * Derived from the @Permission already carried by the endpoint each page
   * reads, rather than invented — the screen cannot render without that call
   * succeeding, so requiring anything else would either lock people out of
   * pages they can use or offer them pages that will only show errors.
   *
   * A route without one is reachable by any signed-in member: the handful of
   * personal screens (home, inbox, profile, search) that everybody has.
   */
  permission?: string;
}

// ---- Organization -----------------------------------------------------------
import { OrgStructurePage } from "@/features/organization/OrgStructurePage";
import { LegalEntitiesPage } from "@/features/organization/LegalEntitiesPage";
import { LegalEntityDetailPage } from "@/features/organization/LegalEntityDetailPage";
import { CompaniesPage } from "@/features/organization/CompaniesPage";
import { CompanyDetailPage } from "@/features/organization/CompanyDetailPage";
import { BranchesPage } from "@/features/organization/BranchesPage";
import { DepartmentsPage } from "@/features/organization/DepartmentsPage";
import { LocationsPage } from "@/features/organization/LocationsPage";

// ---- CRM --------------------------------------------------------------------
import { CrmDashboardPage } from "@/features/crm/CrmDashboardPage";
import { CrmLeadsPage } from "@/features/crm/CrmLeadsPage";
import { CrmOpportunitiesPage } from "@/features/crm/CrmOpportunitiesPage";
import { CrmContactsPage } from "@/features/crm/CrmContactsPage";
import { CrmActivitiesPage } from "@/features/crm/CrmActivitiesPage";
import { CampaignsPage } from "@/features/crm/CampaignsPage";

// ---- Compliance -------------------------------------------------------------
import { CompliancePage } from "@/features/compliance/CompliancePage";
import {
  AuditFilesPage,
  ComplianceCalendarPage,
  DataSubjectRequestsPage,
  EInvoicingPage,
  RegulatoryReportsPage,
  RetentionPoliciesPage,
} from "@/features/compliance/CompliancePages";

// ---- E-commerce -------------------------------------------------------------
import {
  EcommerceOrdersPage,
  EcommercePage,
  ProductFeedsPage,
  StorefrontsPage,
} from "@/features/ecommerce/EcommercePages";

// ---- Planning ---------------------------------------------------------------
import {
  ForecastsPage,
  PlanningMrpPage,
  PlanningPage,
} from "@/features/planning/PlanningPages";

// ---- Configuration governance -----------------------------------------------
import { SodConflictsPage } from "@/features/governance/SodConflictsPage";
import {
  ConfigChangeDetailPage,
  ConfigChangesPage,
  ConfigGovernancePage,
  ConfigVersionsPage,
} from "@/features/governance/ConfigGovernancePages";

export const routeDefs: RouteDef[] = [
  // ---- Organization (12 spec routes; 8 built, see note below) --------------
  {
    path: "/app/organization",
    element: <OrgStructurePage />,
    shell: true,
    permission: "organization.branch.read",
  },
  {
    path: "/app/organization/legal-entities",
    element: <LegalEntitiesPage />,
    shell: true,
    permission: "organization.legal_entity.read",
  },
  {
    path: "/app/organization/legal-entities/:id",
    element: <LegalEntityDetailPage />,
    shell: true,
    permission: "organization.legal_entity.read",
  },
  {
    path: "/app/organization/companies",
    element: <CompaniesPage />,
    shell: true,
    permission: "organization.legal_entity.read",
  },
  {
    path: "/app/organization/companies/:id",
    element: <CompanyDetailPage />,
    shell: true,
    permission: "organization.company.read",
  },
  {
    path: "/app/organization/branches",
    element: <BranchesPage />,
    shell: true,
    permission: "organization.company.read",
  },
  {
    path: "/app/organization/departments",
    element: <DepartmentsPage />,
    shell: true,
    permission: "organization.department.read",
  },
  {
    path: "/app/organization/locations",
    element: <LocationsPage />,
    shell: true,
    permission: "organization.branch.read",
  },
  // Divisions, cost centres and profit centres are specified but have no API
  // and no table. Routing to a page that calls a non-existent endpoint is the
  // exact defect this codebase is full of, so they are left unbuilt until the
  // backend exists.

  // ---- CRM ------------------------------------------------------------------
  {
    path: "/app/crm",
    element: <CrmDashboardPage />,
    shell: true,
    permission: "crm.activity.read",
  },
  {
    path: "/app/crm/leads",
    element: <CrmLeadsPage />,
    shell: true,
    permission: "crm.lead.read",
  },
  {
    path: "/app/crm/opportunities",
    element: <CrmOpportunitiesPage />,
    shell: true,
    permission: "crm.opportunity.read",
  },
  {
    path: "/app/crm/contacts",
    element: <CrmContactsPage />,
    shell: true,
    permission: "crm.lead.read",
  },
  {
    path: "/app/crm/activities",
    element: <CrmActivitiesPage />,
    shell: true,
    permission: "crm.activity.read",
  },
  {
    path: "/app/crm/campaigns",
    element: <CampaignsPage />,
    shell: true,
    permission: "crm.campaign.read",
  },

  // ---- Compliance -----------------------------------------------------------
  {
    path: "/app/compliance",
    element: <CompliancePage />,
    shell: true,
    permission: "compliance.calendar.read",
  },
  {
    path: "/app/compliance/calendar",
    element: <ComplianceCalendarPage />,
    shell: true,
    permission: "compliance.calendar.read",
  },
  {
    path: "/app/compliance/regulatory-reports",
    element: <RegulatoryReportsPage />,
    shell: true,
    permission: "compliance.report.read",
  },
  {
    path: "/app/compliance/e-invoicing",
    element: <EInvoicingPage />,
    shell: true,
    permission: "compliance.einvoicing.read",
  },
  {
    path: "/app/compliance/audit-files",
    element: <AuditFilesPage />,
    shell: true,
    permission: "compliance.audit_file.read",
  },
  {
    path: "/app/compliance/data-subject-requests",
    element: <DataSubjectRequestsPage />,
    shell: true,
    permission: "compliance.dsar.read",
  },
  {
    path: "/app/compliance/retention",
    element: <RetentionPoliciesPage />,
    shell: true,
    permission: "compliance.retention.read",
  },

  // ---- E-commerce -----------------------------------------------------------
  {
    path: "/app/ecommerce",
    element: <EcommercePage />,
    shell: true,
    permission: "ecommerce.order.read",
  },
  {
    path: "/app/ecommerce/storefronts",
    element: <StorefrontsPage />,
    shell: true,
    permission: "ecommerce.storefront.read",
  },
  {
    path: "/app/ecommerce/product-feeds",
    element: <ProductFeedsPage />,
    shell: true,
    permission: "ecommerce.feed.read",
  },
  {
    path: "/app/ecommerce/orders",
    element: <EcommerceOrdersPage />,
    shell: true,
    permission: "ecommerce.order.read",
  },

  // ---- Planning -------------------------------------------------------------
  {
    path: "/app/planning",
    element: <PlanningPage />,
    shell: true,
    permission: "planning.forecast.read",
  },
  {
    path: "/app/planning/forecasts",
    element: <ForecastsPage />,
    shell: true,
    permission: "planning.forecast.read",
  },
  {
    path: "/app/planning/mrp",
    element: <PlanningMrpPage />,
    shell: true,
    permission: "planning.mrp.read",
  },

  // ---- Configuration governance ---------------------------------------------
  {
    path: "/app/governance/sod-conflicts",
    element: <SodConflictsPage />,
    shell: true,
    permission: "sod-rule.manage",
  },
  {
    path: "/app/config/governance",
    element: <ConfigGovernancePage />,
    shell: true,
    permission: "entity-definition.read",
  },
  {
    path: "/app/config/governance/changes",
    element: <ConfigChangesPage />,
    shell: true,
    permission: "entity-definition.read",
  },
  {
    path: "/app/config/governance/changes/:id",
    element: <ConfigChangeDetailPage />,
    shell: true,
    permission: "entity-definition.read",
  },
  {
    path: "/app/config/governance/versions",
    element: <ConfigVersionsPage />,
    shell: true,
    permission: "entity-definition.read",
  },

  // 1-4 Auth (public)
  { path: "/login", element: <LoginPage />, public: true },
  // Second sign-in step. Public because the caller holds only a challenge
  // token, not a session; LoginPage navigates here when mfaRequired.
  { path: "/login/mfa", element: <MfaChallengePage />, public: true },
  { path: "/signup", element: <SignUpPage />, public: true },
  { path: "/verify-email", element: <VerifyEmailPage />, public: true },
  {
    path: "/create-workspace",
    element: <WorkspaceCreationPage />,
    public: true,
  },

  // 5 App Shell anchor + 6-10 shell screens
  {
    path: "/app/home",
    element: <HomeDashboardPage />,
    shell: true,
    permission: "reporting.dashboard.read",
  },
  {
    path: "/app/inbox",
    element: <InboxPage />,
    shell: true,
    permission: "approval.read",
  },
  {
    path: "/app/search",
    element: <SearchResultsPage />,
    shell: true,
    permission: "search.read",
  },
  { path: "/app/notifications", element: <NotificationsPage />, shell: true },
  {
    path: "/app/profile",
    element: <ProfilePage />,
    shell: true,
    permission: "user.read",
  },

  // 11-15 Setup + 88-100 Admin
  { path: "/app/setup", element: <SetupCenterPage />, shell: true },
  {
    path: "/app/setup/wizard",
    element: <ImplementationWizardPage />,
    shell: true,
    permission: "implementation.manage",
  },
  {
    path: "/app/setup/audit-log",
    element: <AuditLogExplorerPage />,
    shell: true,
    permission: "audit.log.read",
  },
  {
    path: "/app/marketplace",
    element: <MarketplacePage />,
    shell: true,
    permission: "marketplace.read",
  },
  {
    path: "/app/setup/api-webhooks",
    element: <ApiWebhooksPage />,
    shell: true,
    permission: "api-client.read",
  },
  {
    path: "/app/setup/users",
    element: <UserManagementPage />,
    shell: true,
    permission: "user.read",
  },
  {
    path: "/app/setup/roles",
    element: <RolesPermissionsPage />,
    shell: true,
    permission: "role.read",
  },
  {
    path: "/app/setup/workflows",
    element: <WorkflowBuilderPage />,
    shell: true,
    permission: "workflow.read",
  },
  {
    path: "/app/setup/approval-matrix",
    element: <ApprovalMatrixDesignerPage />,
    shell: true,
    permission: "approval-matrix.read",
  },
  {
    path: "/app/setup/entity-builder",
    element: <EntityBuilderPage />,
    shell: true,
    permission: "entity-definition.read",
  },
  {
    path: "/app/setup/form-designer",
    element: <FormViewDesignerPage />,
    shell: true,
    permission: "layout.read",
  },
  {
    path: "/app/setup/country-packs",
    element: <CountryPackManagerPage />,
    shell: true,
    permission: "pack.read",
  },
  {
    path: "/app/setup/industry-packs",
    element: <IndustryPackManagerPage />,
    shell: true,
    permission: "pack.read",
  },
  {
    path: "/app/setup/integrations",
    element: <IntegrationCenterPage />,
    shell: true,
    permission: "integration.connector.read",
  },
  {
    path: "/app/setup/data-import",
    element: <DataImportCenterPage />,
    shell: true,
    permission: "import.read",
  },
  {
    path: "/app/setup/tenant",
    element: <TenantSettingsPage />,
    shell: true,
    permission: "tenant.settings.read",
  },
  {
    path: "/app/setup/security",
    element: <SecurityCenterPage />,
    shell: true,
    permission: "audit.log.read",
  },
  {
    path: "/app/setup/billing",
    element: <BillingPage />,
    shell: true,
    permission: "billing.read",
  },

  // Section landing routes.
  //
  // AppShell's top-level nav entries are links, and these six pointed at
  // paths no route rendered — clicking "Sales" or "Inventory" in the sidebar
  // went nowhere. Finance and Projects already had landing pages; the rest
  // redirect to their first child, which is what the section root means.
  // A redirect lands on a screen, never on another redirect: Sales used to
  // open the retired leads page, so "Sales" in the sidebar landed in CRM.
  {
    path: "/app/sales",
    element: <Navigate to="/app/sales/quotations" replace />,
    shell: true,
  },
  {
    path: "/app/procurement",
    element: <Navigate to="/app/procurement/requests" replace />,
    shell: true,
  },
  {
    path: "/app/inventory",
    element: <Navigate to="/app/inventory/products" replace />,
    shell: true,
  },
  {
    path: "/app/manufacturing",
    element: <Navigate to="/app/manufacturing/boms" replace />,
    shell: true,
  },
  {
    path: "/app/hr",
    element: <Navigate to="/app/hr/employees" replace />,
    shell: true,
  },
  {
    path: "/app/reports",
    element: <Navigate to="/app/reports/builder" replace />,
    shell: true,
  },

  // 17-34 Finance
  {
    path: "/app/finance",
    element: <FinanceDashboardPage />,
    shell: true,
    permission: "gl.read",
  },
  {
    path: "/app/finance/accounts",
    element: <ChartOfAccountsPage />,
    shell: true,
    permission: "account.read",
  },
  {
    path: "/app/finance/journal-entries",
    element: <JournalEntriesPage />,
    shell: true,
    permission: "journal-entry.read",
  },
  {
    path: "/app/finance/journal-entries/:id",
    element: <JournalEntryDetailPage />,
    shell: true,
    permission: "journal-entry.read",
  },
  {
    path: "/app/finance/general-ledger",
    element: <GeneralLedgerPage />,
    shell: true,
    permission: "gl.read",
  },
  {
    path: "/app/finance/trial-balance",
    element: <TrialBalancePage />,
    shell: true,
    permission: "gl.trial-balance",
  },
  {
    path: "/app/finance/financial-statements",
    element: <FinancialStatementsPage />,
    shell: true,
    permission: "gl.read",
  },
  {
    path: "/app/finance/customer-invoices",
    element: <CustomerInvoicesPage />,
    shell: true,
    permission: "sales.invoice.read",
  },
  {
    path: "/app/finance/supplier-invoices",
    element: <SupplierInvoicesPage />,
    shell: true,
    permission: "procurement.supplier_invoice.read",
  },
  {
    path: "/app/finance/payments",
    element: <PaymentsPage />,
    shell: true,
    permission: "sales.payment.read",
  },
  {
    path: "/app/finance/bank-reconciliation",
    element: <BankReconciliationPage />,
    shell: true,
    permission: "account.read",
  },
  {
    path: "/app/finance/cash-management",
    element: <CashManagementPage />,
    shell: true,
    permission: "account.read",
  },
  {
    path: "/app/finance/fixed-assets",
    element: <FixedAssetsPage />,
    shell: true,
    permission: "account.read",
  },
  {
    path: "/app/finance/tax",
    element: <TaxDashboardPage />,
    shell: true,
    permission: "tax.read",
  },
  {
    path: "/app/finance/period-close",
    element: <PeriodClosePage />,
    shell: true,
    permission: "period.read",
  },
  {
    path: "/app/finance/exchange-rates",
    element: <ExchangeRatesPage />,
    shell: true,
    permission: "exchange-rate.read",
  },
  {
    path: "/app/finance/budgeting",
    element: <BudgetingPage />,
    shell: true,
    permission: "account.read",
  },
  {
    path: "/app/finance/consolidation",
    element: <ConsolidationPage />,
    shell: true,
    permission: "gl.read",
  },

  // 35-45 Sales
  //
  // Retired (CRM-W01, docs/design/crm.md §5.5): leads and deals had two pages
  // each. The Sales copies read the same rows through a client alias, offered
  // a "New" button and Won/Lost tabs that did nothing, and showed columns the
  // API does not return. A bookmark or an old link still lands on the CRM
  // page; that page asks for its own permission, so these carry none.
  {
    path: "/app/sales/leads",
    element: <Navigate to="/app/crm/leads" replace />,
    shell: true,
  },
  {
    path: "/app/sales/opportunities",
    element: <Navigate to="/app/crm/opportunities" replace />,
    shell: true,
  },
  {
    path: "/app/sales/quotations",
    element: <QuotationsPage />,
    shell: true,
    permission: "sales.quotation.read",
  },
  {
    path: "/app/sales/orders",
    element: <SalesOrdersPage />,
    shell: true,
    permission: "sales.order.read",
  },
  {
    path: "/app/sales/customers",
    element: <CustomersPage />,
    shell: true,
    permission: "sales.customer.read",
  },
  {
    path: "/app/sales/delivery-notes",
    element: <DeliveryNotesPage />,
    shell: true,
    permission: "sales.delivery_note.read",
  },
  {
    path: "/app/sales/invoices",
    element: <SalesInvoicesPage />,
    shell: true,
    permission: "sales.invoice.read",
  },
  {
    path: "/app/sales/returns",
    element: <ReturnsPage />,
    shell: true,
    permission: "sales.return.read",
  },
  {
    path: "/app/sales/price-lists",
    element: <PriceListsPage />,
    shell: true,
    permission: "sales.price_list.read",
  },
  {
    path: "/app/sales/credit-limit-review",
    element: <CreditLimitReviewPage />,
    shell: true,
    permission: "sales.credit_limit.read",
  },
  {
    path: "/app/sales/commissions",
    element: <CommissionsPage />,
    shell: true,
    permission: "sales.commission.read",
  },

  // 46-53 Procurement
  {
    path: "/app/procurement/requests",
    element: <PurchaseRequestsPage />,
    shell: true,
    permission: "procurement.purchase_request.read",
  },
  {
    path: "/app/procurement/rfqs",
    element: <RfqsPage />,
    shell: true,
    permission: "procurement.rfq.read",
  },
  {
    path: "/app/procurement/purchase-orders",
    element: <PurchaseOrdersPage />,
    shell: true,
    permission: "procurement.purchase_order.read",
  },
  {
    path: "/app/procurement/goods-receipts",
    element: <GoodsReceiptsPage />,
    shell: true,
    permission: "procurement.goods_receipt.read",
  },
  {
    path: "/app/procurement/supplier-invoices",
    element: <SupplierInvoicesProcurementPage />,
    shell: true,
    permission: "procurement.supplier_invoice.read",
  },
  {
    path: "/app/procurement/suppliers",
    element: <SuppliersPage />,
    shell: true,
    permission: "procurement.supplier.read",
  },
  {
    path: "/app/procurement/supplier-evaluation",
    element: <SupplierEvaluationPage />,
    shell: true,
    permission: "procurement.supplier_evaluation.read",
  },
  {
    path: "/app/procurement/contracts",
    element: <ProcurementContractsPage />,
    shell: true,
    permission: "contract.read",
  },

  // 54-62 Inventory
  {
    path: "/app/inventory/products",
    element: <ProductsPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/stock",
    element: <StockByLocationPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/movements",
    element: <InventoryMovementsPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/transfers",
    element: <TransfersPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/adjustments",
    element: <AdjustmentsPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/cycle-counts",
    element: <CycleCountsPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/traceability",
    element: <TraceabilityPage />,
    shell: true,
    permission: "inventory.lot.read",
  },
  {
    path: "/app/inventory/reorder-planner",
    element: <ReorderPlannerPage />,
    shell: true,
    permission: "inventory.product.read",
  },
  {
    path: "/app/inventory/valuation",
    element: <ValuationReportPage />,
    shell: true,
    permission: "inventory.product.read",
  },

  // 63-67 Manufacturing
  {
    path: "/app/manufacturing/boms",
    element: <BomsPage />,
    shell: true,
    permission: "manufacturing.bom.read",
  },
  {
    path: "/app/manufacturing/work-centers",
    element: <WorkCentersPage />,
    shell: true,
    permission: "manufacturing.work_center.read",
  },
  {
    path: "/app/manufacturing/production-orders",
    element: <ProductionOrdersPage />,
    shell: true,
    permission: "manufacturing.production_order.read",
  },
  {
    path: "/app/manufacturing/mrp",
    element: <MrpPlannerPage />,
    shell: true,
    permission: "planning.mrp.read",
  },
  {
    path: "/app/manufacturing/quality-inspections",
    element: <QualityInspectionsPage />,
    shell: true,
    permission: "quality.inspection.read",
  },

  // 68-73 HR & Payroll
  {
    path: "/app/hr/employees",
    element: <EmployeeDirectoryPage />,
    shell: true,
    permission: "hr.employee.read",
  },
  {
    path: "/app/hr/employees/:id",
    element: <EmployeeRecordPage />,
    shell: true,
    permission: "hr.employee.read",
  },
  {
    path: "/app/hr/leave",
    element: <LeaveRequestsPage />,
    shell: true,
    permission: "hr.leave.read",
  },
  {
    path: "/app/hr/time-attendance",
    element: <TimeAttendancePage />,
    shell: true,
    permission: "hr.attendance.read",
  },
  {
    path: "/app/hr/payroll-runs",
    element: <PayrollRunsPage />,
    shell: true,
    permission: "payroll.pay_run.read",
  },
  {
    path: "/app/hr/payslips",
    element: <PayslipsPage />,
    shell: true,
    permission: "payroll.payslip.read",
  },

  // 74-77 Projects
  {
    path: "/app/projects",
    element: <ProjectsPage />,
    shell: true,
    permission: "project.read",
  },
  {
    path: "/app/projects/timesheets",
    element: <TimesheetsPage />,
    shell: true,
    permission: "project.timesheet.read",
  },
  {
    path: "/app/projects/expenses",
    element: <ExpenseReportsPage />,
    shell: true,
    permission: "expense.claim.read",
  },
  {
    path: "/app/projects/asset-maintenance",
    element: <AssetMaintenancePage />,
    shell: true,
    permission: "maintenance.work_order.read",
  },

  // 78-79 Services
  {
    path: "/app/services/tickets",
    element: <ServiceTicketsPage />,
    shell: true,
    permission: "service.ticket.read",
  },
  {
    path: "/app/services/appointments",
    element: <ServiceAppointmentsPage />,
    shell: true,
    permission: "service.field.read",
  },

  // 80-81 POS
  {
    path: "/app/pos/sessions",
    element: <PosSessionsPage />,
    shell: true,
    permission: "pos.read",
  },
  {
    path: "/app/pos/order",
    element: <PosOrderPage />,
    shell: true,
    permission: "pos.read",
  },

  // 82-83 Logistics & Fleet
  {
    path: "/app/logistics/shipments",
    element: <ShipmentsPage />,
    shell: true,
    permission: "logistics.shipment.read",
  },
  {
    path: "/app/logistics/fleet",
    element: <FleetVehiclesPage />,
    shell: true,
    permission: "fleet.vehicle.read",
  },

  // 84 Documents
  {
    path: "/app/documents",
    element: <DocumentLibraryPage />,
    shell: true,
    permission: "document.read",
  },

  // 85-87 Reports & BI
  {
    path: "/app/reports/dashboard-builder",
    element: <DashboardBuilderPage />,
    shell: true,
    permission: "reporting.dashboard.read",
  },
  {
    path: "/app/reports/builder",
    element: <ReportBuilderPage />,
    shell: true,
    permission: "reporting.read",
  },
  {
    path: "/app/reports/pivot",
    element: <PivotReportViewerPage />,
    shell: true,
    permission: "reporting.pivot.read",
  },
];
